import { MutableRefObject, useEffect } from 'react';
import type {
  ComponentErrorEvent1,
  ComponentSubmitEvent1,
  JustifiTokenizePaymentMethodCustomEvent,
} from '@justifi/webcomponents/dist/loader';
import { useTranslation } from '@frontend/i18n';
import { classNames } from '@frontend/string';
import { useJustifiOverrideStyles } from '../../hooks/use-justifi-override-styles';
import { useLoadJustifi } from '../../hooks/use-load-justifi';
import {
  JustifiTokenizePaymentOrigParams,
  TokenizePaymentMethodErrorCallbackPayloads,
  TokenizePaymentMethodSubmitEventPayloads,
} from '../../types';
import { inlineStyles, overrideStyles } from './justifi-tokenize-payment-method.styles';

export type JustifiTokenizePaymentMethodProps = JustifiTokenizePaymentOrigParams & {
  className?: string;
  elementRef: MutableRefObject<HTMLJustifiTokenizePaymentMethodElement | undefined>;
};

export const JustifiTokenizePaymentMethod = ({
  accountId,
  authToken,
  disableBankAccount,
  disableCreditCard,
  hideBankAccountBillingForm,
  hideCardBillingForm,
  hideSubmitButton,
  iframeOrigin,
  onError,
  onSubmit,
  paymentMethodGroupId,
  submitButtonText,
  className,
  elementRef,
}: JustifiTokenizePaymentMethodProps) => {
  const { t } = useTranslation('payments');

  const {
    moduleStatuses: {
      paymentMethod: { isLoaded: justifiIsLoaded, hasErrorLoading },
    },
  } = useLoadJustifi(['paymentMethod']);

  useEffect(() => {
    const handleSubmit = (e: JustifiTokenizePaymentMethodCustomEvent<ComponentSubmitEvent1>) => {
      const data = e.detail.response as TokenizePaymentMethodSubmitEventPayloads;

      if ('validationError' in data) {
        return;
      }

      if ('error' in data) {
        return;
      }

      if (data.token) {
        onSubmit?.(data);
        return;
      }

      console.warn("didn't know how to handle justifi submit callback data", data);
    };

    const handleError = (e: JustifiTokenizePaymentMethodCustomEvent<ComponentErrorEvent1>) => {
      const data = e.detail as TokenizePaymentMethodErrorCallbackPayloads;
      onError?.(data);
    };

    if (elementRef?.current) {
      elementRef.current.addEventListener('submit-event', handleSubmit);
      elementRef.current.addEventListener('error-event', handleError);
    }

    return () => {
      if (elementRef?.current) {
        elementRef.current.removeEventListener('submit-event', handleSubmit);
        elementRef.current.removeEventListener('error-event', handleError);
      }
    };
  }, [authToken, accountId, onSubmit]);

  useJustifiOverrideStyles({
    selector: () => elementRef.current?.shadowRoot as HTMLElement | null,
    overrideStyles,
    dependencies: [authToken, accountId],
  });

  if (!authToken || !accountId) {
    return <div>{t('Loading business information . . .')}</div>;
  }

  if (hasErrorLoading) {
    return <div>{t('There was an error loading the payment processor. Please try again later.')}</div>;
  } else if (!justifiIsLoaded) {
    /**
     * No loader for actual component. Just nothing until it loads.
     */
    return null;
  }

  return (
    <div className={classNames('justifi-tokenize-payment-method', className)}>
      <style>
        {/* These styles are done the way justifi wants */}
        {inlineStyles(!!disableBankAccount)}
      </style>
      <justifi-tokenize-payment-method
        ref={elementRef}
        auth-token={authToken}
        account-id={accountId}
        disable-bank-account={disableBankAccount}
        disable-credit-card={disableCreditCard}
        hide-card-billing-form={hideCardBillingForm}
        hide-submit-button={hideSubmitButton}
        hide-bank-account-billing-form={hideBankAccountBillingForm}
        iframe-origin={iframeOrigin}
        payment-method-group-id={paymentMethodGroupId}
        submit-button-text={submitButtonText}
      />
    </div>
  );
};
